/**
 * Contrato dos campos da etiqueta — o par `{ schema, mutability }` do épico
 * #323 §2.1 (fase F1, issue #324).
 *
 * O `mutability` vem de `labelMutability.ts`, que não importa nada (nem zod)
 * para que a suíte de Firestore Rules possa derivar a matriz de casos dele —
 * leia o cabeçalho daquele arquivo antes de mover coisa para cá.
 *
 * `LabelSchema` (em `schemas.ts`) monta os 9 campos a partir de
 * `LabelFieldContract[k].schema`: o contrato é a fonte única, e não uma
 * segunda declaração que envelhece em paralelo.
 */

import { z } from 'zod';
import { ALL_BAIXA_PLATFORMS, ALL_STATUS_CHANGED_VIA, BAIXA_PLATFORM_FALLBACK } from './enums';
import {
  LABEL_BAIXAS_MAX,
  LABEL_FIELD_MUTABILITY,
  LABEL_SCHEMA_VERSION_V2,
  type LabelContractField,
  type LabelFieldMutability,
} from './labelMutability';
// Type-only: `schemas.ts` importa ESTE módulo, então um import de VALOR aqui
// fecharia o ciclo em runtime. Tipo é apagado na emissão — o brand sobrevive
// sem custo.
import type { UserId } from './schemas';

export * from './labelMutability';

/**
 * Id de uma entrada do ledger — gerado no cliente (random), curto.
 *
 * O charset é restrito porque este id vira **chave de mapa** em
 * `movementReceivedAtMillis`, ou seja, um nome de campo do Firestore: um ponto
 * ou uma barra ali transformaria o write do trigger num erro permanente, e o
 * doc entraria em retry infinito. Limitar aqui é de graça — nenhum cliente
 * grava ledger antes da F3.
 */
export const BaixaIdSchema = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[A-Za-z0-9_-]+$/, 'id de baixa só aceita letras, dígitos, `_` e `-`');

/**
 * Onde a entrada nasceu. `.catch('SERVER')` de propósito: um valor novo escrito
 * por um cliente mais recente **não pode** reprovar o array e fazer a etiqueta
 * inteira sumir da lista — foi exatamente esse o bug que a F0 (#330) corrigiu
 * no `status`. Tolerância a enum de wire é regra arquitetural, não transição.
 */
export const BaixaPlatformSchema = z.enum(ALL_BAIXA_PLATFORMS).catch(BAIXA_PLATFORM_FALLBACK);

export const BaixaViaSchema = z.enum(ALL_STATUS_CHANGED_VIA).nullable().catch(null);

/**
 * `byUid` da entrada. `SYSTEM_ACTOR_UID` nas geradas pelo servidor.
 *
 * O `.meta()` existe para o snapshot de contrato do app iOS (#106 §9): sem ele,
 * `z.toJSONSchema(..., { io: 'output' })` não sabe representar o `transform` do
 * brand e o schema inteiro sai vazio. Descreve o que já está no wire — string
 * não-vazia — e não altera parse nem tipo.
 */
export const BaixaActorSchema = z
  .string()
  .min(1)
  .transform((s) => s as UserId)
  .meta({ type: 'string', minLength: 1 });

/**
 * Só entradas sintéticas levam: as do backfill (#331) e as que o trigger cria
 * ao converter um write do protocolo legado. Prova qual execução produziu a
 * linha se um dia aparecer divergência histórica.
 */
export const MigrationRunIdSchema = z.string().min(1).max(60).optional();

/** Movimento: o fato de que uma parte da etiqueta teve um desfecho. */
export const LabelBaixaMovementSchema = z.object({
  kind: z.enum(['CONSUMED', 'DISCARDED', 'EXPIRED']),
  id: BaixaIdSchema,
  /** Na unidade do produto. Sempre positiva — "baixa de zero" não é um fato. */
  quantity: z.number().positive(),
  /** Relógio do DEVICE (event time). No `EXPIRED`, do servidor. */
  atMillis: z.number().int().nonnegative(),
  byUid: BaixaActorSchema,
  byName: z.string().default(''),
  via: BaixaViaSchema,
  platform: BaixaPlatformSchema,
  migrationRunId: MigrationRunIdSchema,
});

/**
 * Estorno: fato compensatório. O ledger é imutável — desfazer NUNCA remove a
 * entrada original, acrescenta esta apontando para ela.
 *
 * `reversesKind` é denormalizado porque as rules não conseguem varrer o array
 * para descobrir o `kind` da entrada estornada: sem ele, não dá para validar
 * que o acumulador certo desceu.
 */
export const LabelBaixaUndoSchema = z.object({
  kind: z.literal('UNDO'),
  id: BaixaIdSchema,
  reverses: BaixaIdSchema,
  reversesKind: z.enum(['CONSUMED', 'DISCARDED', 'EXPIRED']),
  /** = `quantity` da entrada estornada (a linha se lê sozinha no histórico). */
  quantity: z.number().positive(),
  atMillis: z.number().int().nonnegative(),
  byUid: BaixaActorSchema,
  byName: z.string().default(''),
  via: z.literal('UNDO'),
  platform: BaixaPlatformSchema,
  migrationRunId: MigrationRunIdSchema,
});

export const LabelBaixaSchema = z.discriminatedUnion('kind', [
  LabelBaixaMovementSchema,
  LabelBaixaUndoSchema,
]);
export type LabelBaixa = z.infer<typeof LabelBaixaSchema>;
export type LabelBaixaMovement = z.infer<typeof LabelBaixaMovementSchema>;
export type LabelBaixaUndo = z.infer<typeof LabelBaixaUndoSchema>;

type LabelFieldContractEntry = {
  readonly schema: z.ZodType;
  readonly mutability: LabelFieldMutability;
};

/**
 * O contrato completo. Três consumidores: o teste derivado das rules (pelo
 * `mutability`), os guards de cliente e o trigger — que só reconcilia campo
 * marcado `derivedFrom`.
 */
export const LabelFieldContract = {
  quantity: {
    schema: z.number().nonnegative().default(0),
    mutability: LABEL_FIELD_MUTABILITY.quantity,
  },
  consumedQuantity: {
    schema: z.number().nonnegative().default(0),
    mutability: LABEL_FIELD_MUTABILITY.consumedQuantity,
  },
  discardedQuantity: {
    schema: z.number().nonnegative().default(0),
    mutability: LABEL_FIELD_MUTABILITY.discardedQuantity,
  },
  expiredQuantity: {
    schema: z.number().nonnegative().default(0),
    mutability: LABEL_FIELD_MUTABILITY.expiredQuantity,
  },
  remainingQuantity: {
    schema: z.number().nonnegative().default(0),
    mutability: LABEL_FIELD_MUTABILITY.remainingQuantity,
  },
  overrunQuantity: {
    schema: z.number().nonnegative().default(0),
    mutability: LABEL_FIELD_MUTABILITY.overrunQuantity,
  },
  baixas: {
    schema: z.array(LabelBaixaSchema).max(LABEL_BAIXAS_MAX).default([]),
    mutability: LABEL_FIELD_MUTABILITY.baixas,
  },
  labelSchemaVersion: {
    schema: z.number().int().min(1).max(LABEL_SCHEMA_VERSION_V2).default(1),
    mutability: LABEL_FIELD_MUTABILITY.labelSchemaVersion,
  },
  movementReceivedAtMillis: {
    schema: z.record(z.string(), z.number().int().nonnegative()).default({}),
    mutability: LABEL_FIELD_MUTABILITY.movementReceivedAtMillis,
  },
  // `satisfies`, e não anotação de tipo: a anotação alargaria cada `schema`
  // para `z.ZodType` e o `z.infer<typeof LabelSchema>` perderia os tipos dos
  // 9 campos (viravam `unknown`). Assim o shape é conferido e a inferência
  // sobrevive.
} satisfies Record<LabelContractField, LabelFieldContractEntry>;
